// features/usage/components/QuotaAdmin.tsx
import { useState, useCallback } from 'react';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import {
  useQuotaList,
  useAtRisk,
  useCreateQuota,
  useUpdateQuota,
  useGrantCredits,
  useSuspendQuota,
} from '../hooks/useQuotas';
import type { QuotaRow } from '../api/quotaApi';

// ── Formatters ───────────────────────────────────────────────────────────

function fmtCredits(n: number) {
  return n.toLocaleString('en-US');
}

// ── Component ────────────────────────────────────────────────────────────

export default function QuotaAdmin() {
  useDocumentTitle('Quota Admin');

  const quotaList      = useQuotaList();
  const atRisk         = useAtRisk();
  const createMut      = useCreateQuota();
  const updateMut      = useUpdateQuota();
  const grantMut       = useGrantCredits();
  const suspendMut     = useSuspendQuota();

  // ── Create form state ─────────────────────────────────────────────
  const [showCreate, setShowCreate] = useState(false);
  const [createTenantId, setCreateTenantId] = useState('');
  const [createBalance, setCreateBalance]   = useState('');

  const handleCreate = useCallback(() => {
    const tenantId = Number(createTenantId);
    if (!tenantId) return;
    createMut.mutate(
      { tenant_id: tenantId, credit_balance: Number(createBalance) || undefined },
      { onSuccess: () => { setShowCreate(false); setCreateTenantId(''); setCreateBalance(''); } },
    );
  }, [createTenantId, createBalance, createMut]);

  // ── Grant credits state ───────────────────────────────────────────
  const [grantTarget, setGrantTarget]   = useState<number | null>(null);
  const [grantAmount, setGrantAmount]   = useState('');
  const [grantDesc, setGrantDesc]       = useState('');

  const handleGrant = useCallback(() => {
    if (!grantTarget) return;
    const credits = Number(grantAmount);
    if (!credits) return;
    grantMut.mutate(
      { tenantId: grantTarget, payload: { credits, description: grantDesc || undefined } },
      { onSuccess: () => { setGrantTarget(null); setGrantAmount(''); setGrantDesc(''); } },
    );
  }, [grantTarget, grantAmount, grantDesc, grantMut]);

  // ── Edit state ────────────────────────────────────────────────────
  const [editRow, setEditRow]               = useState<QuotaRow | null>(null);
  const [editSoft, setEditSoft]             = useState('');
  const [editHard, setEditHard]             = useState('');

  const startEdit = (q: QuotaRow) => {
    setEditRow(q);
    setEditSoft(String(q.softLimitDaily));
    setEditHard(String(q.hardLimitDaily));
  };

  const handleUpdate = useCallback(() => {
    if (!editRow) return;
    updateMut.mutate(
      { tenantId: editRow.tenantId, payload: {
        soft_limit_daily: Number(editSoft) || undefined,
        hard_limit_daily: Number(editHard) || undefined,
      }},
      { onSuccess: () => setEditRow(null) },
    );
  }, [editRow, editSoft, editHard, updateMut]);

  // ── Suspend toggle ────────────────────────────────────────────────
  const handleSuspend = useCallback((q: QuotaRow) => {
    suspendMut.mutate({ tenantId: q.tenantId, payload: { is_metering_active: !q.isMeteringActive } });
  }, [suspendMut]);

  const quotas   = quotaList.data?.items ?? [];
  const risks    = atRisk.data?.items ?? [];

  return (
    <section className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-neutral-100">Quota Administration</h1>
        <button
          className="rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-500"
          onClick={() => setShowCreate(true)}
        >
          + New Quota
        </button>
      </div>

      {/* ── At-Risk Banner ──────────────────────────────────────────── */}
      {risks.length > 0 && (
        <div className="rounded-md border border-red-700 bg-red-900/30 p-4">
          <h2 className="text-sm font-medium text-red-300 mb-2">
            At-Risk Tenants ({risks.length})
          </h2>
          <div className="space-y-1">
            {risks.map(r => (
              <p key={r.tenantId} className="text-xs text-red-200">
                Tenant {r.tenantId} — {r.todayPctHardLimit.toFixed(1)}% of hard limit
                ({fmtCredits(r.todayRequests)} requests, {fmtCredits(r.creditBalance)} credits)
              </p>
            ))}
          </div>
        </div>
      )}

      {/* ── Create Modal ────────────────────────────────────────────── */}
      {showCreate && (
        <div className="rounded-md pac-glass p-4 border border-neutral-700 space-y-3">
          <h2 className="text-sm font-medium text-neutral-300">Create Quota</h2>
          <div className="flex gap-3 items-end">
            <label className="block">
              <span className="text-xs text-neutral-400">Tenant ID</span>
              <input
                type="number"
                value={createTenantId}
                onChange={e => setCreateTenantId(e.target.value)}
                className="mt-1 block w-32 rounded bg-neutral-800 border border-neutral-700 px-2 py-1 text-sm text-neutral-100"
              />
            </label>
            <label className="block">
              <span className="text-xs text-neutral-400">Initial Balance</span>
              <input
                type="number"
                value={createBalance}
                onChange={e => setCreateBalance(e.target.value)}
                className="mt-1 block w-32 rounded bg-neutral-800 border border-neutral-700 px-2 py-1 text-sm text-neutral-100"
              />
            </label>
            <button
              className="rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-500"
              onClick={handleCreate}
              disabled={createMut.isPending}
            >
              {createMut.isPending ? 'Creating…' : 'Create'}
            </button>
            <button
              className="rounded-md bg-neutral-700 px-3 py-1.5 text-xs font-medium text-neutral-300 hover:bg-neutral-600"
              onClick={() => setShowCreate(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* ── Quota Table ─────────────────────────────────────────────── */}
      {quotaList.isLoading && <p className="text-sm text-neutral-500">Loading quotas…</p>}
      {quotaList.isError && <p className="text-sm text-red-400">Failed to load quotas.</p>}
      {quotas.length > 0 && (
        <div className="rounded-md pac-glass border border-neutral-800 overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead>
              <tr className="border-b border-neutral-800 text-neutral-400 text-xs">
                <th className="px-3 py-2">Tenant</th>
                <th className="px-3 py-2 text-right">Balance</th>
                <th className="px-3 py-2 text-right">Soft Limit</th>
                <th className="px-3 py-2 text-right">Hard Limit</th>
                <th className="px-3 py-2 text-right">Cr/USD</th>
                <th className="px-3 py-2 text-center">Active</th>
                <th className="px-3 py-2 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {quotas.map(q => (
                <tr key={q.tenantId} className="border-b border-neutral-800/50 hover:bg-neutral-800/40">
                  <td className="px-3 py-2 text-neutral-200">{q.tenantId}</td>
                  <td className="px-3 py-2 text-right text-emerald-400">{fmtCredits(q.creditBalance)}</td>
                  <td className="px-3 py-2 text-right text-neutral-300">{fmtCredits(q.softLimitDaily)}</td>
                  <td className="px-3 py-2 text-right text-neutral-300">{fmtCredits(q.hardLimitDaily)}</td>
                  <td className="px-3 py-2 text-right text-neutral-400">{fmtCredits(q.creditsPerUsd)}</td>
                  <td className="px-3 py-2 text-center">
                    <span className={`inline-block w-2 h-2 rounded-full ${q.isMeteringActive ? 'bg-emerald-500' : 'bg-red-500'}`} />
                  </td>
                  <td className="px-3 py-2 text-center space-x-2">
                    <button
                      className="text-xs text-blue-400 hover:text-blue-300"
                      onClick={() => startEdit(q)}
                    >
                      Edit
                    </button>
                    <button
                      className="text-xs text-amber-400 hover:text-amber-300"
                      onClick={() => setGrantTarget(q.tenantId)}
                    >
                      Credits
                    </button>
                    <button
                      className="text-xs text-neutral-400 hover:text-neutral-200"
                      onClick={() => handleSuspend(q)}
                      disabled={suspendMut.isPending}
                    >
                      {q.isMeteringActive ? 'Suspend' : 'Resume'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Edit inline panel ───────────────────────────────────────── */}
      {editRow && (
        <div className="rounded-md pac-glass p-4 border border-neutral-700 space-y-3">
          <h2 className="text-sm font-medium text-neutral-300">
            Edit Quota — Tenant {editRow.tenantId}
          </h2>
          <div className="flex gap-3 items-end">
            <label className="block">
              <span className="text-xs text-neutral-400">Soft Limit Daily</span>
              <input
                type="number"
                value={editSoft}
                onChange={e => setEditSoft(e.target.value)}
                className="mt-1 block w-36 rounded bg-neutral-800 border border-neutral-700 px-2 py-1 text-sm text-neutral-100"
              />
            </label>
            <label className="block">
              <span className="text-xs text-neutral-400">Hard Limit Daily</span>
              <input
                type="number"
                value={editHard}
                onChange={e => setEditHard(e.target.value)}
                className="mt-1 block w-36 rounded bg-neutral-800 border border-neutral-700 px-2 py-1 text-sm text-neutral-100"
              />
            </label>
            <button
              className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-500"
              onClick={handleUpdate}
              disabled={updateMut.isPending}
            >
              {updateMut.isPending ? 'Saving…' : 'Save'}
            </button>
            <button
              className="rounded-md bg-neutral-700 px-3 py-1.5 text-xs font-medium text-neutral-300 hover:bg-neutral-600"
              onClick={() => setEditRow(null)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* ── Grant Credits panel ─────────────────────────────────────── */}
      {grantTarget !== null && (
        <div className="rounded-md pac-glass p-4 border border-neutral-700 space-y-3">
          <h2 className="text-sm font-medium text-neutral-300">
            Grant/Adjust Credits — Tenant {grantTarget}
          </h2>
          <div className="flex gap-3 items-end">
            <label className="block">
              <span className="text-xs text-neutral-400">Credits (negative to deduct)</span>
              <input
                type="number"
                value={grantAmount}
                onChange={e => setGrantAmount(e.target.value)}
                className="mt-1 block w-36 rounded bg-neutral-800 border border-neutral-700 px-2 py-1 text-sm text-neutral-100"
              />
            </label>
            <label className="block">
              <span className="text-xs text-neutral-400">Description</span>
              <input
                type="text"
                value={grantDesc}
                onChange={e => setGrantDesc(e.target.value)}
                className="mt-1 block w-64 rounded bg-neutral-800 border border-neutral-700 px-2 py-1 text-sm text-neutral-100"
              />
            </label>
            <button
              className="rounded-md bg-amber-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-500"
              onClick={handleGrant}
              disabled={grantMut.isPending}
            >
              {grantMut.isPending ? 'Granting…' : 'Grant'}
            </button>
            <button
              className="rounded-md bg-neutral-700 px-3 py-1.5 text-xs font-medium text-neutral-300 hover:bg-neutral-600"
              onClick={() => setGrantTarget(null)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
